import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Loader2, Copy, Download, Send, X, ArrowLeft, Scale, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// -----------------------------------------------------------------------------
// Legal Templates — one-tap Cease-&-Desist / RTI / Consumer complaint
// generator. Backend renders the final text; the UI just handles the form,
// preview and copy/download actions.
// -----------------------------------------------------------------------------
export default function LegalTemplates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);         // template metadata being filled
  const [fields, setFields] = useState({});
  const [ref, setRef] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [speakingResult, setSpeakingResult] = useState(false);

  const speakDraft = () => {
    if (!window.speechSynthesis) { toast.error("Voice not supported"); return; }
    if (speakingResult) { window.speechSynthesis.cancel(); setSpeakingResult(false); return; }
    const u = new SpeechSynthesisUtterance(result.body);
    u.rate = 0.9; u.pitch = 0.9;                 // slower + slightly lower = formal advocate tone
    const voices = window.speechSynthesis.getVoices();
    u.voice = voices.find(v => /en[-_](GB|IN)/i.test(v.lang) && /male/i.test(v.name))
           || voices.find(v => /en[-_](GB|IN)/i.test(v.lang))
           || voices.find(v => /en/i.test(v.lang)) || voices[0];
    u.onend = () => setSpeakingResult(false);
    setSpeakingResult(true);
    window.speechSynthesis.speak(u);
  };

  useSeo({
    title: "Legal Notice Templates · Cease & Desist · RTI · Consumer · find2near",
    description: "One-tap legal draft generator. Cease-&-Desist for trademark infringement, RTI application under RTI Act 2005, Consumer complaint under CPA 2019 — pre-filled with your details.",
    canonical: "/legal-templates",
  });

  useEffect(() => {
    api.get("/legal-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const openTpl = (tpl) => {
    setActive(tpl); setResult(null); setRef("");
    // Pre-populate reasonable defaults from the logged-in user.
    const initial = {};
    tpl.fields.forEach((f) => {
      if (user) {
        if (f.key === "applicant_name" || f.key === "complainant_name" || f.key === "signatory") initial[f.key] = user.name || "";
        if (f.key === "applicant_email") initial[f.key] = user.email || "";
        if (f.key === "applicant_phone" || f.key === "helpdesk_phone") initial[f.key] = user.mobile || "";
      }
    });
    setFields(initial);
  };

  const generate = async () => {
    if (!user) { toast.error("Sign in to generate a draft"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/legal-templates/${active.slug}/generate`, { fields, reference: ref });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to generate"); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result.body); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy blocked — download the .txt instead"); }
  };

  const download = () => {
    const blob = new Blob([result.body], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${result.slug}-${result.reference.replace(/[^A-Za-z0-9]+/g, "_")}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ------------- Result view ------------------------------------------------
  if (result) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6" data-testid="legal-tpl-result">
        <button onClick={() => setResult(null)} className="text-sm font-bold text-stone-500 inline-flex items-center gap-1 mb-3 hover:text-stone-900"><ArrowLeft size={13} /> Back to form</button>
        <div className="rounded-3xl bg-white border border-stone-200 p-5">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">{result.title}</div>
          <div className="text-[10px] font-mono text-stone-500">Ref: {result.reference}</div>
          <pre className="mt-3 whitespace-pre-wrap font-mono text-[12px] leading-relaxed text-stone-800 bg-stone-50 border border-stone-200 rounded-2xl p-4 max-h-[65vh] overflow-y-auto">{result.body}</pre>
          <div className="mt-3 flex gap-2 flex-wrap">
            <Button onClick={copy}     className="rounded-full bg-stone-900 hover:bg-stone-800"><Copy size={13} className="mr-1.5" /> Copy</Button>
            <Button onClick={download} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Download size={13} className="mr-1.5" /> Download .txt</Button>
            <Button onClick={speakDraft} variant="outline" className={`rounded-full ${speakingResult ? "bg-sky-50 text-sky-700 border-sky-300" : ""}`} data-testid="tpl-read-aloud">
              {speakingResult ? <VolumeX size={13} className="mr-1.5" /> : <Volume2 size={13} className="mr-1.5" />}
              {speakingResult ? "Stop reading" : "Read aloud"}
            </Button>
            <Link to="/legal" className="ml-auto text-xs text-red-700 font-bold hover:underline self-center">Full IP notice → /legal</Link>
          </div>
          <div className="text-[10px] text-stone-500 mt-3">
            This draft is a starting point. Please review with a qualified advocate before serving.
            The draft has been logged on your find2near timeline for reference.
          </div>
        </div>
      </div>
    );
  }

  // ------------- Form view --------------------------------------------------
  if (active) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-6" data-testid="legal-tpl-form">
        <button onClick={() => setActive(null)} className="text-sm font-bold text-stone-500 inline-flex items-center gap-1 mb-3 hover:text-stone-900"><ArrowLeft size={13} /> All templates</button>
        <div className="rounded-3xl bg-white border border-stone-200 p-5 space-y-3">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 inline-flex items-center gap-2"><Scale size={12} /> {active.kind.replace(/_/g, " ")} draft</div>
          <h2 className="heading text-2xl font-black text-stone-900">{active.title}</h2>
          <p className="text-sm text-stone-600">{active.description}</p>

          <div className="grid gap-2">
            <Input value={ref} onChange={(e) => setRef(e.target.value)}
                   placeholder="Reference number (leave blank to auto-generate)"
                   className="rounded-2xl h-10 font-mono" data-testid="tpl-ref" />
            {active.fields.map((f) => (
              /queries|reliefs_wanted|deficiency|prior_efforts/.test(f.key)
                ? <textarea key={f.key} value={fields[f.key] || ""} onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                            placeholder={f.label + (f.required ? " *" : "")} rows={4}
                            className="w-full px-4 py-3 rounded-2xl border border-stone-200 outline-none text-sm"
                            data-testid={`tpl-${f.key}`} />
                : <Input key={f.key} value={fields[f.key] || ""} onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                         placeholder={f.label + (f.required ? " *" : "")}
                         className="rounded-2xl h-10" data-testid={`tpl-${f.key}`} />
            ))}
          </div>

          <Button onClick={generate} disabled={busy || !user} className="w-full rounded-full h-11 bg-[#E05E36] hover:bg-[#c94e2a] font-black mt-2" data-testid="tpl-generate">
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}
            {user ? "Generate draft" : "Sign in to generate"}
          </Button>
        </div>
      </div>
    );
  }

  // ------------- List view --------------------------------------------------
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="legal-tpl-page">
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><FileText size={12} className="text-red-600" /> Legal notice templates</div>
        <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">One tap. <span className="text-red-600">Fully-formatted draft.</span></h1>
        <p className="text-stone-500 text-sm mt-1">Cease-&-Desist, RTI applications and Consumer complaints — pre-filled with your info, ready to print.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {templates.map((t) => (
          <button key={t.slug} onClick={() => openTpl(t)}
                  className="text-left rounded-3xl bg-white border border-stone-200 hover:border-red-300 hover:shadow-md p-5 transition-all"
                  data-testid={`tpl-card-${t.slug}`}>
            <div className="w-10 h-10 rounded-2xl bg-red-50 border border-red-200 grid place-items-center text-red-700"><FileText size={18} /></div>
            <div className="mt-3 font-black text-stone-900">{t.title}</div>
            <p className="text-xs text-stone-600 mt-1 line-clamp-4">{t.description}</p>
            <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-red-600">{t.fields.length} field{t.fields.length !== 1 ? "s" : ""} → draft in ~30 s →</div>
          </button>
        ))}
      </div>

      <div className="mt-8 text-[11px] text-stone-500 text-center">
        Templates are convenience drafts — always have a qualified advocate review before serving.
      </div>
    </div>
  );
}
